import { supabase } from '../lib/supabase';

export const clientService = {
  /**
   * Create a new client record (without auth user)
   */
  async createClient(clientData) {
    console.log('=== clientService.createClient called ===');
    console.log('Client data:', clientData);

    try {
      // CRITICAL FIX: Convert empty string email to null
      // Database constraint allows NULL but rejects empty strings
      const email = clientData?.email?.trim() || null;

      console.log('📧 Email processing:', {
        original: clientData?.email,
        processed: email,
        isNull: email === null,
        isEmpty: clientData?.email === ''
      });

      const clientRecord = {
        full_name: clientData?.fullName || clientData?.name,
        email: email, // Use processed email (null if empty)
        phone: clientData?.phone,
        gender: clientData?.gender,
        date_of_birth: clientData?.dateOfBirth,
        age: clientData?.age,
        address: clientData?.address,
        city: clientData?.city,
        state: clientData?.state,
        pincode: clientData?.pincode,
        chief_complaint: clientData?.chiefComplaint,
        medical_history: clientData?.medicalHistory,
        current_medications: clientData?.currentMedications,
        allergies: clientData?.allergies,
        previous_treatments: clientData?.previousTreatments,
        family_medical_history: clientData?.familyMedicalHistory,
        preferred_consultation_mode: clientData?.consultationMode || clientData?.preferredConsultationMode,
        preferred_language: clientData?.preferredLanguage,
        reminder_preferences: clientData?.reminderPreferences,
        emergency_contact_name: clientData?.emergencyContactName,
        emergency_contact_phone: clientData?.emergencyContactPhone,
        emergency_contact_relationship: clientData?.emergencyContactRelationship,
        source: clientData?.source || 'website',
        referral_source: clientData?.referralSource,
        notes: clientData?.notes,
        tags: clientData?.tags
      };

      console.log('Inserting client record:', clientRecord);

      const { data, error } = await supabase
        ?.from('clients')
        ?.insert(clientRecord)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error creating client record:', error);
        console.error('Client error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('Client created successfully:', data);
      return data;
    } catch (error) {
      console.error('=== Error in createClient ===');
      console.error(error);
      throw error;
    }
  },

  /**
   * Get client by phone number
   */
  async getClientByPhone(phone) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.eq('phone', phone)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false })
      ?.limit(1)
      ?.single();

    if (error && error?.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error fetching client by phone:', error);
      throw error;
    }

    return data;
  },

  /**
   * Get client by email
   */
  async getClientByEmail(email) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.eq('email', email)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false })
      ?.limit(1)
      ?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('Error fetching client by email:', error);
      throw error;
    }

    return data;
  },

  /**
   * Get or create client (find existing or create new)
   * @param {Object} clientData - Client information
   * @param {boolean} isPostNatalBooking - Flag to prevent gender updates for post-natal bookings
   */
  async getOrCreateClient(clientData, isPostNatalBooking = false) {
    try {
      console.log('=== getOrCreateClient called ===');
      console.log('Looking for client with phone:', clientData?.phone);
      console.log('Is post-natal booking:', isPostNatalBooking);

      // CRITICAL FIX: Process email - convert empty string to null
      const processedEmail = clientData?.email?.trim() || null;

      console.log('📧 Email processing in getOrCreateClient:', {
        original: clientData?.email,
        processed: processedEmail,
        willSearchByEmail: processedEmail !== null
      });

      // Try to find existing client by phone (primary unique identifier)
      let client = await this.getClientByPhone(clientData?.phone);

      if (client) {
        console.log('Found existing client by phone:', client?.id);
      }

      // If not found by phone, try email (only if email is provided and not empty)
      if (!client && processedEmail) {
        console.log('Client not found by phone, trying email:', processedEmail);
        client = await this.getClientByEmail(processedEmail);

        if (client) {
          console.log('Found existing client by email:', client?.id);
        }
      }

      // If client exists, update their information
      if (client) {
        console.log('Updating existing client:', client?.id);

        // CRITICAL FIX: Use processed email (null if empty) when updating
        const updateEmail = processedEmail || client?.email;

        // CRITICAL FIX: DO NOT update gender for existing patients
        // Gender is a core demographic field that should not change during booking
        // Only update gender if the client record doesn't have one (null/undefined)
        const updateData = {
          full_name: clientData?.fullName || clientData?.name || client?.full_name,
          email: updateEmail, // Use processed email
          chief_complaint: clientData?.chiefComplaint || client?.chief_complaint,
          medical_history: clientData?.medicalHistory || client?.medical_history,
          current_medications: clientData?.currentMedications || client?.current_medications,
          preferred_consultation_mode: clientData?.consultationMode || client?.preferred_consultation_mode,
          reminder_preferences: clientData?.reminderPreferences || client?.reminder_preferences,
          updated_at: new Date().toISOString()
        };

        // Only update gender if existing client doesn't have one
        if (!client?.gender && clientData?.gender) {
          console.log('⚠️ Existing client has no gender - setting gender to:', clientData?.gender);
          updateData.gender = clientData?.gender;
        } else {
          console.log('✅ Preserving existing client gender:', client?.gender);
        }

        const { data: updatedClient, error: updateError } = await supabase
          ?.from('clients')
          ?.update(updateData)
          ?.eq('id', client?.id)
          ?.select()
          ?.single();

        if (updateError) {
          console.error('Error updating client:', updateError);
          throw updateError;
        }

        console.log('Client updated successfully');
        return updatedClient;
      }

      // If no existing client, create new one
      console.log('No existing client found, creating new client');
      const newClient = await this.createClient(clientData);
      console.log('New client created:', newClient?.id);
      return newClient;
    } catch (error) {
      console.error('=== Error in getOrCreateClient ===');
      console.error(error);

      // If it's a unique constraint violation, try to fetch the existing client
      if (error?.code === '23505' && error?.message?.includes('clients_phone_unique')) {
        console.log('Unique constraint violation - fetching existing client by phone');
        const existingClient = await this.getClientByPhone(clientData?.phone);
        if (existingClient) {
          console.log('Found existing client after constraint violation:', existingClient?.id);
          return existingClient;
        }
      }

      throw error;
    }
  },

  /**
   * Update client's appointment count
   * CRITICAL FIX: Supabase JS client doesn't support .raw() method
   * Solution: Use RPC function or fetch-then-update pattern
   */
  async updateClientAppointmentCount(clientId) {
    try {
      // First, fetch the current client data
      const { data: currentClient, error: fetchError } = await supabase
        ?.from('clients')
        ?.select('total_appointments')
        ?.eq('id', clientId)
        ?.single();

      if (fetchError) {
        console.error('Error fetching client for count update:', fetchError);
        throw fetchError;
      }

      // Calculate new count
      const newCount = (currentClient?.total_appointments || 0) + 1;

      // Update with the new count
      const { data, error } = await supabase
        ?.from('clients')
        ?.update({
          total_appointments: newCount,
          last_appointment_date: new Date().toISOString().split('T')[0]
        })
        ?.eq('id', clientId)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating client appointment count:', error);
        throw error;
      }

      console.log(`✅ Client appointment count updated: ${currentClient?.total_appointments} → ${newCount}`);
      return data;
    } catch (error) {
      console.error('Error in updateClientAppointmentCount:', error);
      throw error;
    }
  },

  /**
   * Get client's appointment history
   */
  async getClientAppointments(clientId) {
    const { data, error } = await supabase
      ?.from('appointments')
      ?.select(`
        *,
        staff!therapist_id(
          id,
          user_id,
          role,
          specializations,
          user_profiles!staff_user_id_fkey(full_name, email, phone)
        ),
        treatment:treatment_id(title)
      `)
      ?.eq('client_id', clientId)
      ?.order('preferred_date', { ascending: false });

    if (error) {
      console.error('Error fetching client appointments:', error);
      throw error;
    }

    return data;
  },

  /**
   * Search clients by name or phone (minimum 3 characters)
   * @param {string} searchTerm - Search term (name or phone)
   * @returns {Promise<Array>} Array of matching clients
   */
  async searchClients(searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      return [];
    }

    const { data, error } = await supabase
      ?.from('clients')
      ?.select('id, full_name, phone, email, gender, last_appointment_date, total_appointments')
      ?.eq('is_active', true)
      ?.or(`full_name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
      ?.order('last_appointment_date', { ascending: false, nullsFirst: false })
      ?.limit(10);

    if (error) {
      console.error('Error searching clients:', error);
      throw error;
    }

    return data || [];
  }
};

